/**
 * M3 Gap H — generic preview slot for sandbox-loaded modules.
 *
 * Built-in modules wire their own preview directly through their
 * per-process Configurator (laser → SVGPreview, 3D → MeshPreview).
 * Sandbox modules declare `preview_type` on the manifest; this
 * component renders a slot that matches the declared kind:
 *
 *   - `svg`   — adapter returned an SVG blob; render inline.
 *   - `mesh`  — adapter returned a mesh URL; defer to three.js viewer.
 *   - `none`  — placeholder card; the adapter has no preview surface.
 *
 * The sandbox returns the binary blob over a JSON-RPC channel that
 * the BE pipeline already base64-decodes for us. Today the FE
 * receives the preview through the existing `Quote.analysis_snapshot`
 * surface (laser-style SVG URL field or 3D mesh URL field). Sandbox
 * adapters that ship a real preview should populate
 * `analysis.preview.url` so the renderer below picks it up.
 */

import { useTranslation } from "react-i18next";

type PreviewKind = "svg" | "mesh" | "none";

interface PreviewAnalysis {
  preview?:
    | {
        url?: string | null;
        content_type?: string | null;
      }
    | null;
}

export interface DynamicPreviewProps {
  previewType?: PreviewKind | null;
  analysis: PreviewAnalysis | null;
}

export function DynamicPreview({
  previewType,
  analysis,
}: DynamicPreviewProps): JSX.Element {
  const { t } = useTranslation("quote");
  const kind = (previewType ?? "none") as PreviewKind;
  const url = analysis?.preview?.url ?? null;

  if (kind === "none" || !url) {
    return (
      <div
        className="flex h-40 items-center justify-center rounded border border-dashed border-border bg-surface-2 text-xs text-ink-3"
        data-testid="dynamic-preview-placeholder"
      >
        {t("dynamicPreview.placeholder", {
          defaultValue: "Modul neposkytuje preview.",
        })}
      </div>
    );
  }

  if (kind === "svg") {
    return (
      <div
        className="overflow-hidden rounded border border-border bg-surface-2 p-2"
        data-testid="dynamic-preview-svg"
      >
        <img
          src={url}
          alt={t("dynamicPreview.svgAlt", {
            defaultValue: "Náhled dílu (SVG)",
          })}
          className="mx-auto max-h-72 object-contain"
        />
      </div>
    );
  }

  // ``mesh`` — defer the rendering to the existing
  // ``<model-viewer>``-style three.js viewer when the FE wires it.
  // For now we emit a generic anchor + content-type hint so the
  // operator can click through to the GLB; future M3 iteration will
  // swap this for an inline three.js canvas. Keeps the slot
  // functional rather than a hidden 0-byte div.
  return (
    <div
      className="flex flex-col gap-1 rounded border border-border bg-surface-2 p-3 text-xs"
      data-testid="dynamic-preview-mesh"
    >
      <span className="text-ink-2">
        {t("dynamicPreview.meshHint", {
          defaultValue: "3D model:",
        })}
      </span>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all font-mono text-[11px] text-accent hover:underline"
      >
        {url}
      </a>
      {analysis?.preview?.content_type && (
        <span className="text-ink-3">
          ({analysis.preview.content_type})
        </span>
      )}
    </div>
  );
}

export default DynamicPreview;
